import { useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthProvider'
import {
  WORK_UNITS,
  requiresClimate,
  loadDailyWorkContext,
  saveDailyWorkContext,
  type WorkUnitKey,
} from '../lib/dailyContext'

const MOODS = [
  { score: 1, emoji: '😞', label: 'Muito mal' },
  { score: 2, emoji: '😟', label: 'Não muito bem' },
  { score: 3, emoji: '😐', label: 'Neutro' },
  { score: 4, emoji: '🙂', label: 'Bem' },
  { score: 5, emoji: '😄', label: 'Muito bem' },
] as const

export function DailyWorkCheckin() {
  const { profile, offlineProfile } = useAuth()
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [unit, setUnit] = useState<WorkUnitKey | null>(null)
  const [step, setStep] = useState<'unit' | 'climate'>('unit')
  const [error, setError] = useState('')

  useEffect(() => {
    if (!profile?.ativo || offlineProfile || !navigator.onLine) return
    let mounted = true

    loadDailyWorkContext()
      .then((context) => {
        if (mounted && !context) setOpen(true)
      })
      .catch(() => {
        // O Suite continua disponível caso a migration ainda não tenha sido aplicada.
      })

    return () => { mounted = false }
  }, [profile?.id, profile?.ativo, offlineProfile])

  async function chooseUnit(nextUnit: WorkUnitKey) {
    setUnit(nextUnit)
    setError('')

    if (requiresClimate(nextUnit)) {
      setStep('climate')
      return
    }

    setLoading(true)
    try {
      await saveDailyWorkContext(nextUnit, null)
      setOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível registrar a unidade.')
    } finally {
      setLoading(false)
    }
  }

  async function chooseMood(score: number) {
    if (!unit) return
    setLoading(true)
    setError('')
    try {
      await saveDailyWorkContext(unit, score)
      setOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível registrar sua resposta.')
    } finally {
      setLoading(false)
    }
  }

  if (!open) return null

  return (
    <div className="daily-checkin-backdrop" role="presentation">
      <section className="daily-checkin-modal" role="dialog" aria-modal="true" aria-labelledby="daily-checkin-title">
        <header>
          <div>
            <p className="eyebrow">CHECK-IN DIÁRIO</p>
            <h2 id="daily-checkin-title">
              {step === 'unit' ? 'Onde você está trabalhando hoje?' : 'Como você está se sentindo hoje?'}
            </h2>
          </div>
          <button className="daily-checkin-close" type="button" onClick={() => setOpen(false)} aria-label="Responder depois">×</button>
        </header>

        {step === 'unit' ? (
          <>
            <p className="daily-checkin-help">Selecione onde você está trabalhando hoje. Essa informação ajuda a interpretar o uso do Suite por unidade.</p>
            <div className="daily-unit-grid">
              {WORK_UNITS.map((item) => (
                <button key={item.key} type="button" disabled={loading} onClick={() => chooseUnit(item.key)}>
                  <strong>{item.label}</strong>
                  <small>{item.climate ? 'Participa da pesquisa de clima' : 'Sem pesquisa de clima'}</small>
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <p className="daily-checkin-help">Sua resposta compõe somente indicadores agregados de clima da operação. O painel de clima não expõe respostas nominais.</p>
            <div className="daily-mood-grid">
              {MOODS.map((mood) => (
                <button key={mood.score} type="button" disabled={loading} onClick={() => chooseMood(mood.score)}>
                  <span aria-hidden="true">{mood.emoji}</span>
                  <strong>{mood.label}</strong>
                </button>
              ))}
            </div>
            <button className="text-button daily-back-button" type="button" disabled={loading} onClick={() => setStep('unit')}>← Alterar unidade</button>
          </>
        )}

        {error && <div className="notice error">{error}</div>}
        {loading && <p className="daily-checkin-saving">Salvando...</p>}
      </section>
    </div>
  )
}
